import { esc } from '../ui/modal';
import { t } from '../ui/lang';
import { MAC_VM_NAME, macAdbConnectCmd, macAdbTcpipCmd, macAutostartCmd, macDockerDesktopInstallCmd, macDockerDesktopRun, macNoSleepCmd, macUtmInstallCmd, macVmIpCmd, } from './farm-docker-config';
import { downloadMd } from './farm-setup-shared';
/**
 * macOS host = Ubuntu VM in UTM running the regular Linux recipe (Docker Desktop for
 * Mac has no USB passthrough), so the Docker steps reuse the Linux sub-tab's commands
 * verbatim and only the VM/USB plumbing is added. The Docker Desktop alternative skips the
 * VM and serves phones on Wi-Fi adb only.
 */
export function buildMacSetup(name, key, std) {
    const cmds = {
        utm: macUtmInstallCmd(),
        install: std.installCmd,
        updater: std.updaterCmd,
        cleanup: std.cleanupCmd,
        run: std.dockerRun,
        noSleep: macNoSleepCmd(),
        autostart: macAutostartCmd(),
        ip: macVmIpCmd(),
        ddInstall: macDockerDesktopInstallCmd(),
        ddTcpip: macAdbTcpipCmd(),
        ddConnect: macAdbConnectCmd(),
        ddRun: macDockerDesktopRun({ relayHost: window.location.origin, key }),
    };
    const block = (cmd) => `\`\`\`bash\n${cmd}\n\`\`\``;
    const mdContent = `# Farm setup (macOS) — ${name}

Docker Desktop for Mac can't pass USB phones into a container, so the farm runs on
Docker Engine inside an Ubuntu VM in UTM — the same recipe as on Linux — and UTM shares
the phones with the VM.

**Steps marked "in the VM" run inside the Ubuntu VM, not in Mac Terminal** — on the Mac
itself \`docker\`, \`usermod\` and get.docker.com don't work.

## 1. Install UTM (Mac Terminal)

Needs Homebrew; alternatively download UTM from https://mac.getutm.app.

${block(cmds['utm'])}

## 2. Create the VM (UTM)

- **Create a New Virtual Machine → Virtualize → Linux.** Leave **Use Apple Virtualization**
  off: only the QEMU backend can share USB devices.
- Boot ISO: Ubuntu Server 24.04 — **arm64** on Apple Silicon (https://ubuntu.com/download/server/arm),
  **amd64** on Intel (https://ubuntu.com/download/server).
- At least 4 GB RAM, 2 CPU cores and 32 GB disk. Name the VM **${MAC_VM_NAME}**.
- Before the first boot, open the VM settings: **Network → Bridged**, and in **Input**
  keep USB sharing on and raise **Maximum Shared USB Devices** to the number of phones.
- Install Ubuntu Server with **OpenSSH server**. The UTM console has no clipboard, so log in
  from Mac Terminal instead: get the IP with \`hostname -I\` in the console, then
  \`ssh <user>@<VM_IP>\` — commands paste there with Cmd+V.

## 3. Install Docker (in the VM)

${block(cmds['install'])}

## 4. Create the self-update helper (in the VM)

${block(cmds['updater'])}

## 5. Stop and remove the existing container and image (in the VM)

For a manual update start here, then re-run step 6.

${block(cmds['cleanup'])}

## 6. Start the container (in the VM)

Recordings land in the current directory, so run it from your home folder.

${block(cmds['run'])}

## 7. Share USB phones with the VM (UTM)

Plug the phone in and, when UTM asks, connect it to **${MAC_VM_NAME}** — or pick it in the
USB menu of the VM window toolbar. Repeat after replugging the cable.

## 8. Keep the Mac awake (Mac Terminal)

Run it on its own and type the Mac password when asked.

${block(cmds['noSleep'])}

## 9. Start the VM at logon (Mac Terminal)

Turn on automatic login (System Settings → Users & Groups) so the farm comes back after a reboot.

${block(cmds['autostart'])}

## 10. Initial setup

Get the VM's IP inside the VM and open **http://<VM_IP>:8000** in a browser on the Mac.
Check the relay connection in **Settings** and allow USB debugging on each phone.

${block(cmds['ip'])}

## Alternative: Docker Desktop (phones on Wi-Fi adb only)

No VM: the container runs in Docker Desktop. It can't see USB phones, so each phone joins
over Wi-Fi adb and must be on the same network as the Mac. adb over Wi-Fi turns off when the
phone reboots — repeat step A2 after every reboot. **Restart with update** from the panel
doesn't work here: update by running the step 5 commands in Mac Terminal, then step A3.

### A1. Install Docker Desktop and adb (Mac Terminal)

Start Docker Desktop once and turn on **Settings → General → Start Docker Desktop when you sign in**.

${block(cmds['ddInstall'])}

### A2. Switch each phone to Wi-Fi adb (Mac Terminal)

The phone has to be plugged into the Mac with a USB cable: this step runs in Mac Terminal, never
through \`docker exec\` — the container can't see USB. Turn on USB debugging on the phone, plug it in,
set the USB mode to **File transfer** and allow the Mac when the phone asks. \`adb devices\` must list
the phone as \`device\`; then unplug the cable.

${block(cmds['ddTcpip'])}

- \`no devices/emulators found\` — the Mac doesn't see the phone: try a data cable (not charge-only)
  and another port, check the USB mode; on Xiaomi/Redmi/POCO also turn on
  **USB debugging (Security settings)**.
- \`unauthorized\` — unlock the phone and allow the Mac in the prompt.

### A3. Start the container (Mac Terminal)

Recordings land in the current directory, so run it from your home folder.

${block(cmds['ddRun'])}

### A4. Add the phones (Mac Terminal)

The phone and the Mac must be on the same Wi-Fi network (a router or another phone's hotspot).
Take the IP from the phone's Wi-Fi settings — not from \`ping\` or the router: on a hotspot the router
is the hotspot phone. Only if the farm phone itself shares internet to the Mac is its IP the Mac's
router (\`ipconfig getoption en0 router\`). Replace \`<PHONE_IP>\` and run:

${block(cmds['ddConnect'])}

\`nc\` must print \`succeeded\`. \`Connection refused\` means the phone is reachable but adb isn't
listening on Wi-Fi — it was rebooted or step A2 didn't go through: repeat A2. Instead of
\`docker exec\` you can also add the phone at **http://localhost:8000** → **Available Network Devices →
manually add** → \`<PHONE_IP>:5555\`. The container uses the Mac's adb key, so no new prompt is needed.
`;
    return { cmds, mdContent };
}
/** Steps in order; `copy` names the command shown under the step (UTM-only steps have none). */
const STEPS = [
    { text: 'farms.mac_step1', copy: 'utm' },
    { text: 'farms.mac_step2' },
    { text: 'farms.mac_step3', copy: 'install' },
    { text: 'farms.mac_step4', copy: 'updater' },
    { text: 'farms.mac_step5', copy: 'cleanup' },
    { text: 'farms.mac_step6', copy: 'run' },
    { text: 'farms.mac_step7' },
    { text: 'farms.mac_step8', copy: 'noSleep' },
    { text: 'farms.mac_step9', copy: 'autostart' },
    { text: 'farms.mac_step10', copy: 'ip' },
];
const DD_STEPS = [
    { text: 'farms.mac_dd_step1', copy: 'ddInstall' },
    { text: 'farms.mac_dd_step2', copy: 'ddTcpip' },
    { text: 'farms.mac_dd_step3', copy: 'ddRun' },
    { text: 'farms.mac_dd_step4', copy: 'ddConnect' },
];
/** Inner HTML for the macOS sub-tab; copy/download wired by {@link wireMacSetup}. */
export function macSetupHtml(s) {
    const stepsHtml = (list) => list.map((st, i) => `
        <p class="modal__text"${i ? ' style="margin-top:12px"' : ''}>${t(st.text, { vm: MAC_VM_NAME })}</p>
        ${st.copy ? `<div class="launch-block">
            <pre class="launch-pre">${esc(s.cmds[st.copy] ?? '')}</pre>
            <button class="tbl-btn copy-key-btn" data-mac-copy="${st.copy}" title="${t('common.copy')}">⎘ ${t('common.copy')}</button>
        </div>` : ''}`).join('');
    return `
        <p class="modal__text" style="margin-bottom:12px">${t('farms.mac_intro')}</p>
        ${stepsHtml(STEPS)}
        <ul class="modal__text" style="margin:6px 0 0 18px;line-height:1.7">
            <li>${t('farms.launch_li1')}</li>
            <li>${t('farms.launch_li2')}</li>
            <li>${t('farms.launch_li3')}</li>
        </ul>
        <p class="modal__text" style="margin-top:20px"><strong>${t('farms.mac_dd_title')}</strong></p>
        <p class="modal__text" style="margin-bottom:12px">${t('farms.mac_dd_intro')}</p>
        ${stepsHtml(DD_STEPS)}
        <div class="modal__actions">
            <button type="button" class="tbl-btn" data-mac-download style="margin-right:auto">${t('farms.download_md')}</button>
        </div>
    `;
}
export function wireMacSetup(root, s, name) {
    root.querySelectorAll('[data-mac-copy]').forEach((btn) => {
        btn.addEventListener('click', () => {
            const cmd = s.cmds[btn.dataset['macCopy'] ?? ''];
            if (cmd === undefined)
                return;
            void navigator.clipboard.writeText(cmd);
            btn.textContent = `✓ ${t('common.copied')}`;
            setTimeout(() => { btn.textContent = `⎘ ${t('common.copy')}`; }, 1500);
        });
    });
    root.querySelector('[data-mac-download]')?.addEventListener('click', () => {
        downloadMd(s.mdContent, `farm-setup-macos-${name}`);
    });
}
